/* The cookie banner's behaviour (snippets/dc-cookie-banner.liquid, V2.21 Phase 7).
 *
 * Shopify's Customer Privacy API is the record of consent. This file asks it whether the
 * visitor has to be asked, shows the banner if so, writes the choice back, and tells the
 * theme's own Google tag (layout/theme.liquid) what was chosen. Shopify's tracking and the app
 * pixels read the same record themselves; nothing here talks to them.
 *
 *   statistics  -> analytics   -> Google analytics_storage
 *   marketing   -> marketing   -> Google ad_storage, ad_user_data, ad_personalization
 *   preferences -> preferences -> Google functionality_storage, personalization_storage
 *
 * Three things learned from the API on this store on 2026-10-13, each of which the obvious code
 * gets wrong:
 *
 *   - shouldShowBanner() says whether this visitor's region requires a banner. It stays true
 *     after they have chosen, so "have they chosen" is read from currentVisitorConsent().
 *   - The choice is kept in an HttpOnly cookie. A script cannot read it before the API has
 *     loaded, which takes about half a second: too late for the first page view of a returning
 *     visitor. So the choice is mirrored in localStorage ("dc-consent"), layout/theme.liquid
 *     reads the mirror in the same script that sets Google's defaults, and this file corrects
 *     the mirror from Shopify's record on every page. The mirror never grants anything Shopify's
 *     record does not: it is rewritten here before the visitor can act.
 *   - A visitor outside the regions that require consent is never asked and counts as allowed.
 *     Google's default is "denied" everywhere, so they are told "granted" here.
 *
 * "Cookie settings" in the footer (sections/dc-footer.liquid, snippets/dc-m-footer.liquid)
 * opens the choices again with the current answers ticked. No library.
 */
(function () {
  var banner = document.querySelector('dc-cookie-banner');
  if (!banner || !window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

  var MIRROR = 'dc-consent';
  var GOOGLE = {
    analytics: ['analytics_storage'],
    marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
    preferences: ['functionality_storage', 'personalization_storage'],
  };
  var choices = banner.querySelector('.dc-cookie-choices');
  var chooseBtn = banner.querySelector('[data-dc-cookie-choose]');
  var saveBtn = banner.querySelector('[data-dc-cookie-save]');
  var boxes = {
    analytics: banner.querySelector('input[name="analytics"]'),
    marketing: banner.querySelector('input[name="marketing"]'),
    preferences: banner.querySelector('input[name="preferences"]'),
  };
  var returnFocus = null;

  function privacy() { return window.Shopify.customerPrivacy; }

  /** What may run for this visitor now: true where they agreed, or were never required to. */
  function allowed() {
    var p = privacy();
    return {
      analytics: !!p.analyticsProcessingAllowed(),
      marketing: !!p.marketingAllowed(),
      preferences: !!p.preferencesProcessingAllowed(),
    };
  }

  /** Has this visitor answered? (Not shouldShowBanner(): see the note at the top.) */
  function answered() {
    var c = privacy().currentVisitorConsent() || {};
    return !!(c.analytics || c.marketing || c.preferences);
  }

  function tellGoogle(state) {
    if (typeof window.gtag !== 'function') return;
    var update = {};
    Object.keys(GOOGLE).forEach(function (key) {
      GOOGLE[key].forEach(function (name) { update[name] = state[key] ? 'granted' : 'denied'; });
    });
    window.gtag('consent', 'update', update);
  }

  /** Keep the mirror equal to Shopify's record, or absent while the visitor has not answered. */
  function mirror() {
    try {
      var p = privacy();
      if (p.shouldShowBanner() && !answered()) window.localStorage.removeItem(MIRROR);
      else window.localStorage.setItem(MIRROR, JSON.stringify(allowed()));
    } catch (e) { /* storage blocked: the next page waits for the API instead */ }
  }

  function sync() { tellGoogle(allowed()); mirror(); }

  // The phone buy bar and the cart toast sit above the banner rather than under it
  // (assets/dc-interaction.css reads --dc-cookie-h).
  function measure() {
    var h = banner.hidden ? 0 : Math.ceil(banner.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--dc-cookie-h', h + 'px');
  }

  function showChoices(on) {
    choices.hidden = !on;
    chooseBtn.hidden = on;
    saveBtn.hidden = !on;
    chooseBtn.setAttribute('aria-expanded', on ? 'true' : 'false');
    measure();
  }

  function open(withChoices) {
    var now = privacy().currentVisitorConsent() || {};
    Object.keys(boxes).forEach(function (key) { boxes[key].checked = now[key] === 'yes'; });
    banner.hidden = false;
    showChoices(!!withChoices);
    if (withChoices) boxes.analytics.focus();
  }

  function close() {
    banner.hidden = true;
    measure();
    if (returnFocus && document.contains(returnFocus)) returnFocus.focus();
    returnFocus = null;
  }

  function save(state) {
    banner.setAttribute('aria-busy', 'true');
    privacy().setTrackingConsent(
      { analytics: state.analytics, marketing: state.marketing, preferences: state.preferences, sale_of_data: state.marketing },
      function () {
        banner.removeAttribute('aria-busy');
        sync();
        close();
      }
    );
  }

  banner.addEventListener('click', function (event) {
    var t = event.target.closest('button');
    if (!t) return;
    if (t.hasAttribute('data-dc-cookie-accept')) save({ analytics: true, marketing: true, preferences: true });
    else if (t.hasAttribute('data-dc-cookie-reject')) save({ analytics: false, marketing: false, preferences: false });
    else if (t.hasAttribute('data-dc-cookie-choose')) { showChoices(true); boxes.analytics.focus(); }
    else if (t.hasAttribute('data-dc-cookie-save')) save({ analytics: boxes.analytics.checked, marketing: boxes.marketing.checked, preferences: boxes.preferences.checked });
  });

  // Escape folds the choices away, or closes a banner the visitor reopened themselves. It never
  // dismisses the first question: that needs an answer, and "no" is one click.
  banner.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    if (answered()) close();
    else if (!choices.hidden) { showChoices(false); chooseBtn.focus(); }
  });

  // Whatever changes the banner's height changes what stands on it: a rotation, the choices
  // opening, or a test tool hiding the banner outright.
  window.addEventListener('resize', measure);
  if ('ResizeObserver' in window) new window.ResizeObserver(measure).observe(banner);

  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
    if (error || !privacy()) return;
    sync();
    if (privacy().shouldShowBanner() && !answered()) open(false);

    // The footer's "Cookie settings": shown now that there is something for it to open.
    document.querySelectorAll('.dc-cookie-settings').forEach(function (el) { el.hidden = false; });
    document.addEventListener('click', function (event) {
      var link = event.target.closest('.dc-cookie-settings button');
      if (!link) return;
      returnFocus = link;
      open(true);
    });

    // Another script may record consent too (the checkout, an app): follow Shopify's record.
    document.addEventListener('visitorConsentCollected', function () { sync(); });
  });
})();
